import { useCallback, useEffect, useState } from 'react';
import { api } from '../../shell/bridge';
import { SkeletonBlock } from '../../ui/Skeleton';
import { RevealCard, type RevealCardData } from '../../components/RevealCard';
import './reveal-history.css';

/* Response shape for GET /api/questions/history — the worker replies with
   { reveals: RevealPayload[] } in NO guaranteed order (a probe on 2026-10-17
   got 16, 14, 15 for three dev-dated days), so the screen sorts by dateKey
   rather than trusting the reply. */
type RevealedQuestion = RevealCardData & {
  question: RevealCardData['question'] & {
    state: 'pending' | 'revealed';
    deadlineAt: number;
    revealedAt: number | null;
  };
};

interface RevealHistoryProps {
  members: { memberId: string; displayName: string }[];
  onClose: () => void;
}

/**
 * Question reveal history (card YMMXiFOdFgdF) — every revealed question with
 * both answers, newest first, straight from /api/questions/history (the API's
 * own order is not guaranteed, so the list sorts by dateKey descending and
 * the lede names the order).
 *
 * Card XRKkcYceOxq9 restructured the entry: one BOUNDED card per question
 * (category chip + date + prompt + the two answers side by side with the
 * partner labels pinned at the top of each column), because the old stacked
 * blocks left comparison — the point of a daily question — to the reader.
 *
 * Founder email 2026-10-27: that card is now <RevealCard>, shared with the
 * inline reveal list on Us > Questions and with the thread's question card, so
 * the initial/attached reveal and the full reveal are one implementation.
 *
 * Renders inside the .pear-panel App.tsx wraps it in, so this block only
 * lays itself out; the panel owns the scrollbar.
 */
export function RevealHistory({ members, onClose }: RevealHistoryProps) {
  const [reveals, setReveals] = useState<RevealedQuestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setReveals(null);
    try {
      const data = await api<{ reveals?: RevealedQuestion[] }>('/api/questions/history');
      setReveals(Array.isArray(data.reveals) ? data.reveals : []);
    } catch {
      setError('The Reveal History Could Not Be Loaded.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Same convention as the Us panel: a member id reads as its display name. */
  const nameFor = (memberId: string): string =>
    members.find(m => m.memberId === memberId)?.displayName ?? 'Unknown';

  /** Column order = the couple's member order, so both cards read the same
   *  way down the history. */
  const order = members.map(m => m.memberId);

  return (
    <section className="rh" aria-labelledby="rh-title">
      <div className="rh-head">
        <h2 className="rh-title" id="rh-title">
          Reveal history
        </h2>
        <button
          type="button"
          className="rh-close"
          id="btn-reveal-history-close"
          onClick={onClose}
        >
          Close
        </button>
      </div>

      <p className="rh-lede">
        Every question you have opened together, most recent first. Each answer is signed by the
        person who wrote it.
      </p>

      {error && (
        <div className="rh-state" role="alert">
          <p className="rh-error">{error}</p>
          <button type="button" className="rh-retry" onClick={() => void load()}>
            Try again
          </button>
        </div>
      )}

      {!error && reveals === null && (
        <div className="rh-state" aria-live="polite">
          <p className="rh-loading-text">Loading reveal history</p>
          <SkeletonBlock lines={4} />
        </div>
      )}

      {!error && reveals !== null && reveals.length === 0 && (
        <p className="rh-empty">
          Nothing has been revealed yet. Answer today&apos;s question and it lands here once
          you have both written.
        </p>
      )}

      {!error && reveals !== null && reveals.length > 0 && (
        <ol className="rh-list">
          {/* Newest first, sorted by the couple's own day — NOT by reversing
              whatever order the worker happened to return (2026-10-17 email,
              and probe-ui-batch caught the API replying 16, 14, 15: a reverse
              of an unguaranteed order is still an unguaranteed order). The
              lede names the order out loud. */}
          {[...reveals]
            .sort((a, b) => (b.question.dateKey < a.question.dateKey ? -1 : 1))
            .map(r => (
              <li className="rh-item" key={r.question.questionId}>
                <RevealCard
                  reveal={r}
                  order={order}
                  nameFor={nameFor}
                  emptyText="No answers arrived before this reveal opened."
                  testId="reveal-history"
                />
              </li>
            ))}
        </ol>
      )}
    </section>
  );
}
